/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box, useMediaQuery, useTheme } from "@mui/material";
import React, { useMemo } from "react";

import { Button } from "../../../components/Button";
import { Loader } from "../../../components/Loader";
import TestIds from "../../../testIds";
import { SideContainer } from "../../products/components/ProductCard";
import { ProductCardParent } from "../../products/components/ProductCard/ProductCardParent";
import { Product } from "../../products/storage";
import { useProductStatus } from "../../products/utils/useProductStatus";
import { SEED_MIX_TYPE } from "../types/SeedOrderFormType";

import { useAddSeedModal } from "./AddSeedModal";

interface SeedSearchProductCardProps {
  item: Product;
}

export const SeedSearchProductCard = ({ item }: SeedSearchProductCardProps) => {
  const { callback, seedType } = useAddSeedModal();

  const [loading, setLoading] = React.useState(false);

  const handleAddToMix = () => {
    setLoading(true);
    callback(item).then(() => {
      setLoading(false);
    });
  };

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const styles: React.CSSProperties = {
    marginTop: isSmall ? 3 : "auto",
    transition: "all 0.5s ease",
    whiteSpace: "nowrap",
  };
  const buttonText = useMemo(
    () => (seedType === SEED_MIX_TYPE.PREMIX.toString() ? "Add to order" : "Add to mix"),
    [seedType]
  );

  const loadingText = useMemo(
    () => `Adding to ${seedType === SEED_MIX_TYPE.PREMIX.toString() ? "order" : "mix"}...`,
    [seedType]
  );

  const { canPurchase } = useProductStatus(item.lifeCycleState, item.basePriceExcGst);

  const renderQuickOrderButton = React.useMemo(() => {
    if (!canPurchase) {
      return null;
    }
    if (loading) {
      return (
        <Box sx={styles}>
          <Loader type="inline-flex" title={loadingText} />
        </Box>
      );
    }
    return (
      <Button
        key="product-cart-button"
        variant="contained"
        onClick={handleAddToMix}
        sx={styles}
        data-testid={TestIds.feat.seed.seedSearch.addToMix}
      >
        {buttonText}
      </Button>
    );
  }, [handleAddToMix, loading, canPurchase]);

  const renderSideContainer = React.useMemo(() => {
    if (!isSmall) {
      return <SideContainer>{renderQuickOrderButton}</SideContainer>;
    }
  }, [isSmall, renderQuickOrderButton]);

  return (
    <ProductCardParent
      disableLink
      item={item}
      renderQuickOrderButton={renderQuickOrderButton}
      renderSideContainer={renderSideContainer}
      showImage={false}
    />
  );
};
